import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { ThemeProvider, CssBaseline, useMediaQuery } from "@mui/material";
import { PaletteMode } from "@mui/material";
import { createAppTheme } from "../../theme";

/**
 * User-selectable theme preference. "auto" follows the operating system's
 * color-scheme setting; "light" / "dark" pin the theme explicitly.
 */
export type ThemeMode = "light" | "dark" | "auto";

const STORAGE_KEY = "teamray.themeMode";

interface ThemeModeContextValue {
  /** The user's stored preference (light / dark / auto). */
  mode: ThemeMode;
  /** The palette actually being rendered, after resolving "auto". */
  resolvedMode: PaletteMode;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(
  undefined
);

const readStoredMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "auto") {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (private mode, SSR); fall back to auto.
  }
  return "auto";
};

/**
 * Provides the MUI theme based on the user's light/dark/auto preference and
 * exposes a setter via {@link useThemeMode}. Persists the choice to
 * localStorage and reacts live to OS color-scheme changes while in "auto".
 */
export const ThemeModeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);

  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)", {
    noSsr: true,
  });

  const resolvedMode: PaletteMode = useMemo(() => {
    if (mode === "auto") {
      return prefersDark ? "dark" : "light";
    }
    return mode;
  }, [mode, prefersDark]);

  const setMode = (next: ThemeMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ignore persistence failures; the in-memory state still applies.
    }
  };

  // Keep the native color-scheme and the body background gradient in sync so
  // form controls, scrollbars and the page behind the app match the theme.
  useEffect(() => {
    const root = document.documentElement;
    root.style.colorScheme = resolvedMode;
    // Ambient "gradient mesh": a couple of soft cyan/violet radial glows over
    // a base wash, giving the frosted cards an atmospheric backdrop.
    root.style.setProperty(
      "--app-background",
      resolvedMode === "dark"
        ? [
            "radial-gradient(900px 600px at 12% -5%, rgba(14,134,212,0.18), transparent 60%)",
            "radial-gradient(800px 700px at 95% 8%, rgba(139,92,246,0.16), transparent 60%)",
            "radial-gradient(700px 700px at 50% 110%, rgba(91,141,239,0.10), transparent 60%)",
            "linear-gradient(160deg, #080b12 0%, #0a0e16 45%, #0b0f18 100%)",
          ].join(", ")
        : [
            "radial-gradient(900px 600px at 10% -5%, rgba(14,134,212,0.14), transparent 60%)",
            "radial-gradient(800px 700px at 95% 5%, rgba(139,92,246,0.12), transparent 60%)",
            "radial-gradient(700px 700px at 50% 115%, rgba(91,141,239,0.10), transparent 60%)",
            "linear-gradient(160deg, #eaf2fa 0%, #eef3f9 45%, #f1eefb 100%)",
          ].join(", ")
    );
  }, [resolvedMode]);

  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  const value = useMemo(
    () => ({ mode, resolvedMode, setMode }),
    [mode, resolvedMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = (): ThemeModeContextValue => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeModeProvider");
  }
  return context;
};
